import { ArrowLeft, ExternalLink, Sparkles } from 'lucide-react'
import { Reveal } from '@/shared/motion/reveal'
import { Section, SectionHeader } from '@/shared/ui/section'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { mediaUrl } from '@/shared/lib/media'

/**
 * Projects — public-facing showcase of products Abdallah has shipped.
 *
 * Why a dedicated section: courses + tools + prompts already sit on
 * the home page. Real production apps Abdallah designed and built
 * deserve their own surface so they're treated as first-class
 * portfolio entries (not buried in a tutorial library).
 */
const T = defineT({
  eyebrow: { ar: 'مشاريعي', en: 'My Projects' },
  title: {
    ar: 'منتجات حقيقية شغّالة في السوق',
    en: 'Real products shipped in the wild',
  },
  description: {
    ar: 'مش بس بعلّم الناس — كمان ببني وبنشر منتجات بنفسي. ده ميدان مفتوح للتطبيق الفعلي للـ AI، التصميم، والتطوير.',
    en: 'I don’t just teach — I ship. These are products I designed and built end-to-end, where AI, design, and engineering meet real users.',
  },
  visit: { ar: 'زور الموقع', en: 'Visit site' },
  // Moharb
  moharbName: { ar: 'مُحارب', en: 'Moharb' },
  moharbTagline: {
    ar: 'أول تطبيق عربي للتعافي',
    en: 'First Arabic recovery app',
  },
  moharbDesc: {
    ar: 'تطبيق مبني على علم الأعصاب، التلعيب، ومجتمع دعم مجهول — لمساعدة الشباب العرب على التعافي من إدمان الإباحية. أنا اللي صمّمت الـ UX، طوّرت الواجهة، وكتبت محتوى المدرّب الذكي بالعربي.',
    en: 'A neuroscience-driven, gamified, anonymous-community recovery app for Arab youth fighting porn addiction. I designed the UX, shipped the frontend, and wrote the Arabic AI coach.',
  },
  moharbTags: {
    ar: 'تصميم · تطوير · AI · محتوى عربي',
    en: 'Design · Engineering · AI · Arabic content',
  },
})

interface Project {
  key: 'moharb'
  href: string
  logo: string
  cover?: string
  /** HSL triplet (no `hsl()` wrapper) for the brand glow halo. */
  accentHsl: string
  nameKey: keyof typeof T
  taglineKey: keyof typeof T
  descKey: keyof typeof T
  tagsKey: keyof typeof T
}

const PROJECTS: Project[] = [
  {
    key: 'moharb',
    href: 'https://moharb-app.com',
    logo: mediaUrl('projects/moharb-logo.webp'),
    cover: mediaUrl('projects/moharb-dashboard.webp'),
    // Soft warrior-red — ties to the app's combat metaphor without
    // clashing with the site primary (#F97A1F amber).
    accentHsl: '5 75% 55%',
    nameKey: 'moharbName',
    taglineKey: 'moharbTagline',
    descKey: 'moharbDesc',
    tagsKey: 'moharbTags',
  },
]

export function Projects() {
  const { t, isRtl } = useTranslation(T)

  return (
    <Section id="projects" pad="md">
      <Reveal>
        <SectionHeader
          eyebrow={t('eyebrow')}
          title={t('title')}
          description={t('description')}
        />
      </Reveal>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1 lg:gap-8">
        {PROJECTS.map((project) => (
          <Reveal key={project.key}>
            <a
              href={project.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t(project.nameKey)} — ${t(project.taglineKey)}`}
              className={cn(
                'group relative isolate overflow-hidden rounded-3xl border border-border bg-card',
                'card-hover',
                'grid gap-0 lg:grid-cols-[1.1fr_1fr]',
              )}
              style={{ ['--accent' as string]: project.accentHsl }}
            >
              {/* Brand-tinted glow */}
              <span
                aria-hidden
                className="pointer-events-none absolute -end-24 -top-24 h-64 w-64 rounded-full opacity-50 blur-3xl transition-opacity duration-500 group-hover:opacity-90"
                style={{ background: `hsl(var(--accent) / 0.6)` }}
              />

              {/* Left — text */}
              <div className="relative flex flex-col p-7 sm:p-9 lg:p-10">
                <div className="flex items-center gap-4">
                  <span
                    className="grid h-14 w-14 place-items-center overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
                    aria-hidden
                  >
                    <img
                      src={project.logo}
                      alt=""
                      width={40}
                      height={40}
                      loading="lazy"
                      decoding="async"
                      className="h-10 w-10 object-contain"
                    />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl font-extrabold tracking-tight">
                      {t(project.nameKey)}
                    </h3>
                    <p
                      className="text-sm font-medium"
                      style={{ color: `hsl(var(--accent))` }}
                    >
                      {t(project.taglineKey)}
                    </p>
                  </div>
                </div>

                <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {t(project.descKey)}
                </p>

                <p className="mt-4 inline-flex items-center text-xs uppercase tracking-wider text-muted-foreground/80">
                  <Sparkles className="me-1.5 h-3 w-3" aria-hidden />
                  {t(project.tagsKey)}
                </p>

                <span className="mt-auto inline-flex items-center gap-2 pt-7 text-sm font-semibold text-primary">
                  {t('visit')}
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  <ArrowLeft
                    className={cn(
                      'h-4 w-4 transition-transform duration-300',
                      isRtl
                        ? 'group-hover:-translate-x-1'
                        : 'rotate-180 group-hover:translate-x-1',
                    )}
                    aria-hidden
                  />
                </span>
              </div>

              {/* Right — visual */}
              {project.cover && (
                <div className="relative isolate flex items-end overflow-hidden bg-gradient-to-br from-card via-card to-secondary/40 lg:items-center">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-30"
                    style={{
                      background: `radial-gradient(circle at 65% 50%, hsl(var(--accent) / 0.5), transparent 60%)`,
                    }}
                  />
                  <img
                    src={project.cover}
                    alt=""
                    width={390}
                    height={844}
                    loading="lazy"
                    decoding="async"
                    className="relative z-10 mx-auto h-64 w-auto max-w-full object-contain p-6 transition-transform duration-700 group-hover:scale-[1.04] sm:h-80 lg:h-96"
                  />
                </div>
              )}
            </a>
          </Reveal>
        ))}
      </div>
    </Section>
  )
}
